import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

/**
 * /my/welcome — page publique créateur.
 *
 * Affichée après un login TikTok RÉUSSI quand le compte n'est PAS rattaché à
 * une agence Black (flag `known=false` renvoyé par la fonction tiktok-auth).
 * C'est notamment ce que voit le reviewer TikTok (compte test non enregistré) :
 * le login aboutit, on montre le profil TikTok de base + l'usage des données +
 * les liens légaux (privacy / terms). Pas de données agence (R2).
 */
const MyWelcome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tiktok, setTiktok] = useState({ stats: null, videos: [] });

  useEffect(() => {
    // Garde : un créateur CONNU ne devrait pas atterrir ici → portail.
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user || user.user_metadata?.role !== 'creator') {
        navigate('/my/login', { replace: true });
        return;
      }
      if (user.user_metadata?.known !== false) {
        navigate('/my/dashboard', { replace: true });
        return;
      }
      try {
        const cached = localStorage.getItem('creator_profile');
        setProfile(cached ? JSON.parse(cached) : {
          display_name: user.user_metadata?.tiktok_display_name,
          avatar_url: user.user_metadata?.tiktok_avatar_url,
        });
      } catch { /* ignore */ }
      try {
        const tk = localStorage.getItem('creator_tiktok');
        if (tk) {
          const parsed = JSON.parse(tk);
          setTiktok({ stats: parsed.stats ?? null, videos: Array.isArray(parsed.videos) ? parsed.videos : [] });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const logout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('creator_profile');
    navigate('/my/login', { replace: true });
  };

  const name = profile?.display_name || profile?.username || t('myWelcome.creator', 'Creator');
  const fmtNum = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return String(v);
  };
  const s = tiktok.stats || {};
  const videos = tiktok.videos || [];

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8 flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E5202A] flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 3h6v6H3zM11 3h6v6h-6zM3 11h6v6H3zM11 11h6v6h-6z" fill="white" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
            NoiRock
          </h1>
        </div>

        <div className="rounded-2xl p-7 border border-white/8 text-center" style={{ backgroundColor: '#111111' }}>
          {/* Avatar */}
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-16 h-16 rounded-full mx-auto mb-4 object-cover border border-white/10" />
          ) : (
            <div className="w-16 h-16 rounded-full mx-auto mb-4 bg-white/5 border border-white/10 flex items-center justify-center text-white/40 text-xl font-semibold">
              {String(name).slice(0, 1).toUpperCase()}
            </div>
          )}

          <h2 className="text-white text-lg font-semibold mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('myWelcome.hello', 'Welcome')}, {name}
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-5">
            {t('myWelcome.body', 'You are signed in with TikTok. Your account is not yet linked to a partner agency on NoiRock. Once an agency adds you, your performance dashboard will appear here.')}
          </p>

          {/* Stats publiques TikTok (scope user.info.stats) */}
          {tiktok.stats && (
            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                [t('myWelcome.followers', 'Followers'), s.follower_count],
                [t('myWelcome.likes', 'Likes'), s.likes_count],
                [t('myWelcome.videos', 'Videos'), s.video_count],
              ].map(([label, val]) => (
                <div key={label} className="rounded-xl bg-white/[0.03] border border-white/8 py-3">
                  <div className="text-white text-base font-semibold" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtNum(val)}</div>
                  <div className="text-white/40 text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Vidéos récentes (scope video.list) */}
          {videos.length > 0 && (
            <div className="mb-5 text-left">
              <div className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                {t('myWelcome.recentVideos', 'Recent videos')}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {videos.slice(0, 6).map((v) => (
                  <a key={v.id} href={v.share_url} target="_blank" rel="noopener noreferrer"
                     className="relative block rounded-lg overflow-hidden bg-white/5 aspect-[9/16] group">
                    {v.cover_image_url && (
                      <img src={v.cover_image_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    <span className="absolute bottom-1 left-1 right-1 text-white text-[10px] font-medium drop-shadow flex items-center gap-1">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 110-8 4 4 0 010 8z"/></svg>
                      {fmtNum(v.view_count)}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white/[0.03] border border-white/8 px-4 py-3 mb-5 text-left">
            <p className="text-white/40 text-xs leading-relaxed">
              {t('myWelcome.dataNote', 'NoiRock uses your TikTok public profile, stats and video list only to identify your creator account and show your activity. We never post, upload or modify content on your behalf.')}
            </p>
          </div>

          <button
            onClick={logout}
            className="w-full px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 text-sm hover:bg-white/10 hover:text-white transition-all"
          >
            {t('myWelcome.signOut', 'Sign out')}
          </button>
        </div>

        {/* Liens légaux — requis review TikTok */}
        <div className="text-center mt-5 flex items-center justify-center gap-4 text-xs text-white/30">
          <a href="/privacy.html" className="hover:text-white/60 transition-colors">{t('myWelcome.privacy', 'Privacy Policy')}</a>
          <span>·</span>
          <a href="/terms.html" className="hover:text-white/60 transition-colors">{t('myWelcome.terms', 'Terms of Service')}</a>
        </div>
      </div>
    </div>
  );
};

export default MyWelcome;
